import styled, { css, keyframes } from 'styled-components';

// ============================================================================
// ANIMATIONS
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// ============================================================================
// DESIGN TOKENS
// ============================================================================

const colors = {
  // Backgrounds
  bgPrimary: '#0a0e17',
  bgSecondary: '#111827',
  bgTertiary: '#1a2235',
  bgCard: 'rgba(17, 24, 39, 0.8)',
  bgCardHover: 'rgba(26, 34, 53, 0.9)',
  bgInput: 'rgba(15, 23, 42, 0.7)',
  bgInputFocus: 'rgba(15, 23, 42, 0.95)',

  // Borders
  borderDefault: 'rgba(55, 65, 81, 0.5)',
  borderHover: 'rgba(75, 85, 99, 0.7)',
  borderFocus: '#3b82f6',
  borderSubtle: 'rgba(55, 65, 81, 0.3)',

  // Text
  textPrimary: '#f9fafb',
  textSecondary: '#9ca3af',
  textMuted: '#6b7280',
  textLabel: '#94a3b8',

  // Accents
  primary: '#3b82f6',
  primaryHover: '#2563eb',
  primaryLight: 'rgba(59, 130, 246, 0.15)',
  success: '#10b981',
  successLight: 'rgba(16, 185, 129, 0.15)',
  warning: '#f59e0b',
  warningLight: 'rgba(245, 158, 11, 0.15)',
  danger: '#ef4444',
  dangerLight: 'rgba(239, 68, 68, 0.15)',
  purple: '#8b5cf6',

  // Gradients
  gradientPrimary: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
  gradientSuccess: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  gradientCard: 'linear-gradient(180deg, rgba(26, 34, 53, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
};

// ============================================================================
// CONTAINER AND LAYOUT
// ============================================================================

export const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: ${colors.bgPrimary};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  display: flex;
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  min-width: 0;
`;

export const MainContent = styled.div`
  padding: 2rem 2.5rem;
  flex: 1;
  min-width: 0;
  margin: 0 auto;
  max-width: 1600px;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  gap: 12px;
`;

// ============================================================================
// TICKET HEADER
// ============================================================================

export const TicketHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-bottom: 1.5rem;
  padding: 1.25rem 1.75rem;
  background: ${colors.gradientCard};
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid ${colors.borderSubtle};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
`;

export const TicketInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TicketLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const TicketNumber = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.4rem 0.875rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &[style*="cursor: pointer"] {
    background: ${colors.primaryLight};
    border: 1px solid rgba(59, 130, 246, 0.25);

    &:hover {
      background: rgba(59, 130, 246, 0.25);
      border-color: rgba(59, 130, 246, 0.5);
      color: #60a5fa;
      transform: translateY(-1px);
    }
  }
`;

export const TicketStatusWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const TicketStatusLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: right;
`;

export const TicketStatus = styled.div<{ status?: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;

  ${props => {
    switch (props.status?.toUpperCase()) {
      case 'DRAFT':
        return css`
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
          color: #a5b4fc;
          border: 1px solid rgba(99, 102, 241, 0.3);
          &::before { content: '●'; font-size: 8px; }
        `;
      case 'SENT':
        return css`
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(234, 88, 12, 0.15) 100%);
          color: #fbbf24;
          border: 1px solid rgba(245, 158, 11, 0.3);
          &::before { content: '◐'; font-size: 10px; animation: ${pulse} 2s infinite; }
        `;
      case 'EXECUTED':
        return css`
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
          color: #34d399;
          border: 1px solid rgba(16, 185, 129, 0.3);
          &::before { content: '✓'; font-size: 10px; }
        `;
      case 'SETTLED':
        return css`
          background: linear-gradient(135deg, rgba(5, 150, 105, 0.2) 0%, rgba(4, 120, 87, 0.2) 100%);
          color: #10b981;
          border: 1px solid rgba(5, 150, 105, 0.4);
          &::before { content: '✓✓'; font-size: 9px; letter-spacing: -2px; }
        `;
      case 'CANCELLED':
      case 'REJECTED':
        return css`
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(185, 28, 28, 0.15) 100%);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
          &::before { content: '✕'; font-size: 10px; }
        `;
      default:
        return css`
          background: rgba(75, 85, 99, 0.2);
          color: ${colors.textSecondary};
          border: 1px solid rgba(75, 85, 99, 0.3);
        `;
    }
  }}
`;

// ============================================================================
// CARD AND SECTION
// ============================================================================

export const TicketCard = styled.div`
  background: ${colors.gradientCard};
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid ${colors.borderSubtle};
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 1px rgba(255, 255, 255, 0.1) inset;
`;

export const TicketSection = styled.div`
  padding: 1.75rem 2rem;
  border-bottom: 1px solid ${colors.borderSubtle};

  &:last-child {
    border-bottom: none;
  }
`;

export const TicketSectionTitle = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 1.5rem 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${colors.borderSubtle};

  &:first-child {
    margin-top: 0;
  }
`;

// ============================================================================
// FORM ELEMENTS - MODERNIZED
// ============================================================================

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.textLabel};
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  gap: 4px;

  /* Required indicator styling */
  &::after {
    color: ${colors.primary};
  }
`;

const inputStyles = css<{ error?: boolean }>`
  padding: 0.875rem 1rem;
  background: ${colors.bgInput};
  border: 1.5px solid ${props => props.error ? 'rgba(239, 68, 68, 0.5)' : colors.borderDefault};
  border-radius: 10px;
  color: ${colors.textPrimary};
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled):not(:focus) {
    border-color: ${props => props.error ? 'rgba(239, 68, 68, 0.6)' : colors.borderHover};
    background: ${colors.bgInputFocus};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#ef4444' : colors.borderFocus};
    background: ${colors.bgInputFocus};
    box-shadow: 0 0 0 3px ${props => props.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.15)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: rgba(15, 23, 42, 0.4);
  }

  &::placeholder {
    color: ${colors.textMuted};
    font-weight: 400;
  }
`;

export const Input = styled.input<{ error?: boolean }>`
  ${inputStyles}
`;

export const Select = styled.select<{ error?: boolean }>`
  ${inputStyles}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 40px;

  option {
    background: ${colors.bgSecondary};
    color: ${colors.textPrimary};
    padding: 12px;
  }
`;

export const FieldError = styled.div`
  color: #f87171;
  font-size: 11px;
  font-weight: 600;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '!';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    background: rgba(239, 68, 68, 0.2);
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
  }
`;

// ============================================================================
// AUTOCOMPLETE
// ============================================================================

export const AutocompleteWrapper = styled.div`
  position: relative;
`;

export const AutocompleteDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: ${colors.bgSecondary};
  border: 1px solid ${colors.borderDefault};
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  z-index: 100;
  max-height: 280px;
  overflow-y: auto;
  animation: ${fadeIn} 0.2s ease-out;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.borderDefault};
    border-radius: 3px;
  }
`;

export const SuggestionItem = styled.div<{ active?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${colors.borderSubtle};
  background: ${props => props.active ? colors.primaryLight : 'transparent'};
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.primaryLight};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const SuggestionCusip = styled.div`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-weight: 700;
  color: #60a5fa;
  font-size: 13px;
  letter-spacing: 0.02em;
`;

export const SuggestionSecurity = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  margin-top: 3px;
  font-weight: 500;
`;

// ============================================================================
// FOOTER AND BUTTONS
// ============================================================================

export const TicketFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.25rem 1.75rem;
  background: ${colors.gradientCard};
  border-radius: 14px;
  border: 1px solid ${colors.borderSubtle};
`;

const buttonBase = css`
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: ${colors.gradientPrimary};
  color: white;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.45);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  background: ${colors.bgTertiary};
  color: ${colors.textSecondary};
  border: 1px solid ${colors.borderDefault};

  &:hover:not(:disabled) {
    background: ${colors.bgCardHover};
    color: ${colors.textPrimary};
    border-color: ${colors.borderHover};
  }
`;

export const CancelButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: ${colors.textMuted};

  &:hover {
    color: #f87171;
    background: rgba(239, 68, 68, 0.1);
  }
`;

// ============================================================================
// SIDEBAR NAVIGATION
// ============================================================================

export const Sidebar = styled.div`
  width: 260px;
  background: ${colors.bgSecondary};
  border-right: 1px solid ${colors.borderSubtle};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

export const SidebarItem = styled.div<{ active?: boolean; disabled?: boolean }>`
  padding: 1rem 1.5rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background: ${props => props.active ? colors.primaryLight : 'transparent'};
  border-left: 3px solid ${props => props.active ? colors.primary : 'transparent'};
  color: ${props => props.active ? colors.textPrimary : props.disabled ? colors.textMuted : colors.textSecondary};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 0.875rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: ${props => props.disabled ? 0.4 : 1};

  &:hover:not([disabled]) {
    background: ${props => props.active ? colors.primaryLight : 'rgba(255, 255, 255, 0.03)'};
    color: ${props => props.active ? colors.textPrimary : '#e2e8f0'};
  }
`;

// ============================================================================
// IMPACT AND ANALYSIS
// ============================================================================

export const ImpactPanel = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: ${colors.primaryLight};
  border-radius: 14px;
  border: 1px solid rgba(59, 130, 246, 0.2);
`;

export const ImpactTitle = styled.h4`
  font-size: 0.8125rem;
  font-weight: 700;
  color: #60a5fa;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.875rem;
`;

export const ImpactItem = styled.div`
  padding: 0.875rem;
  background: ${colors.bgInput};
  border-radius: 10px;
  border: 1px solid ${colors.borderSubtle};
`;

export const ImpactLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

export const ImpactValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${props => {
    if (props.positive) return '#4ade80';
    if (props.negative) return '#f87171';
    return colors.textPrimary;
  }};
`;

// ============================================================================
// ACTION BUTTONS
// ============================================================================

export const ActionButton = styled.button<{ variant?: 'primary' | 'danger' | 'secondary' }>`
  ${buttonBase}
  padding: 0.625rem 1.25rem;
  font-size: 0.8125rem;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return css`
          background: ${colors.primary};
          color: white;
          &:hover:not(:disabled) { background: ${colors.primaryHover}; }
        `;
      case 'danger':
        return css`
          background: ${colors.dangerLight};
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
          &:hover:not(:disabled) { background: rgba(239, 68, 68, 0.25); }
        `;
      default:
        return css`
          background: ${colors.bgTertiary};
          color: ${colors.textSecondary};
          border: 1px solid ${colors.borderDefault};
          &:hover:not(:disabled) { background: ${colors.bgCardHover}; color: ${colors.textPrimary}; }
        `;
    }
  }}
`;

export const StatusValue = styled.div<{ status?: string }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(75, 85, 99, 0.2);
  color: ${colors.textSecondary};
  border: 1px solid rgba(75, 85, 99, 0.3);
`;

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const ActionButtonsWrapper = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${colors.borderSubtle};
`;

export const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

// ============================================================================
// MESSAGES
// ============================================================================

export const SuccessMessage = styled.div`
  padding: 1rem 1.25rem;
  background: ${colors.successLight};
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 10px;
  color: #34d399;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: rgba(16, 185, 129, 0.2);
    border-radius: 50%;
    font-size: 11px;
  }
`;

export const ErrorSummary = styled.div`
  padding: 1rem 1.25rem;
  background: ${colors.dangerLight};
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  color: #f87171;
  margin-bottom: 1.25rem;
`;

export const ErrorSummaryTitle = styled.h4`
  font-size: 0.8125rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '!';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    background: rgba(239, 68, 68, 0.3);
    border-radius: 50%;
    font-size: 11px;
  }
`;

export const ErrorList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.8125rem;
  font-weight: 500;

  li {
    margin-bottom: 0.25rem;
    &:last-child { margin-bottom: 0; }
  }
`;

export const FullWidthSection = styled.div`
  width: 100%;
`;

export const ExpanderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(55, 65, 81, 0.3);
  border-radius: 8px;
  cursor: pointer;
  color: #f9fafb;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
  margin-top: 1rem;

  &:hover {
    background: rgba(30, 41, 59, 0.6);
    border-color: rgba(75, 85, 99, 0.6);
  }

  svg {
    color: #94a3b8;
    transition: transform 0.2s ease;
  }
`;

export const ExpanderContent = styled.div`
  padding: 16px 0 0 0;
  animation: fadeIn 0.3s ease-out;
`;

// ============================================================================
// ALLOCATIONS
// ============================================================================

export const AllocationPanel = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: ${colors.bgCard};
  border-radius: 16px;
  border: 1px solid ${colors.borderSubtle};
`;

export const AllocationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;

export const AllocationTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0;
`;

export const ToggleButton = styled.button<{ active?: boolean }>`
  ${buttonBase}
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => props.active ? css`
    background: ${colors.primary};
    color: white;
  ` : css`
    background: transparent;
    color: ${colors.textMuted};
    border: 1px solid ${colors.borderDefault};
    &:hover { color: ${colors.textPrimary}; border-color: ${colors.borderHover}; }
  `}
`;

export const AllocationGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 80px;
  gap: 0.875rem;
  margin-bottom: 1rem;
`;

export const AllocationGridHeader = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${colors.borderSubtle};
`;

export const AllocationRow = styled.div`
  display: contents;
`;

export const AllocationInput = styled(Input)`
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
`;

export const AllocationSelect = styled(Select)`
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
`;

export const AllocationRemoveButton = styled.button`
  ${buttonBase}
  padding: 0.5rem;
  background: transparent;
  color: #f87171;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 0.75rem;

  &:hover:not(:disabled) {
    background: ${colors.dangerLight};
    border-color: rgba(239, 68, 68, 0.3);
  }
`;

export const AddAllocationButton = styled.button`
  ${buttonBase}
  padding: 0.75rem;
  background: transparent;
  color: ${colors.primary};
  border: 1.5px dashed rgba(59, 130, 246, 0.4);
  width: 100%;
  margin-bottom: 1rem;

  &:hover:not(:disabled) {
    background: ${colors.primaryLight};
    border-color: ${colors.primary};
  }
`;

export const AllocationSummary = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${colors.borderSubtle};
`;

export const AllocationSummaryLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${colors.textMuted};
  text-transform: uppercase;
`;

export const AllocationSummaryValue = styled.span<{ valid?: boolean }>`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.valid ? '#34d399' : '#f87171'};
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
`;

// ============================================================================
// FILE UPLOAD
// ============================================================================

export const FileUploadArea = styled.div`
  position: relative;
  border: 2px dashed ${colors.borderDefault};
  border-radius: 14px;
  padding: 2rem;
  text-align: center;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
    background: ${colors.primaryLight};
  }

  input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }
`;

export const FileUploadText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.textMuted};
  font-size: 0.875rem;
  font-weight: 500;

  span { font-size: 1.5rem; }
`;

export const FileList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: ${colors.bgInput};
  border: 1px solid ${colors.borderSubtle};
  border-radius: 10px;
`;

export const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const FileIcon = styled.div`
  font-size: 1.125rem;
`;

export const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FileName = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${colors.textPrimary};
`;

export const FileSize = styled.span`
  font-size: 0.6875rem;
  color: ${colors.textMuted};
`;

export const FileRemoveButton = styled.button`
  background: transparent;
  color: ${colors.textMuted};
  border: none;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s;

  &:hover {
    color: #f87171;
    background: ${colors.dangerLight};
  }
`;

export const TextArea = styled.textarea`
  ${inputStyles as any}
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
`;

export const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
